import { Hero } from "../components/home/Hero";
import { CategoryGrid } from "../components/home/CategoryGrid";
import { NextSteps } from "../components/home/NextSteps";
import { CalculatorCard } from "../components/shared/CalculatorCard";
import { ArticleCard } from "../components/shared/ArticleCard";
import { NewsletterSignup } from "../components/shared/NewsletterSignup";
import { useSeo } from "../lib/seo";
import { useJsonLd, buildOrganizationSchema, buildWebSiteSchema } from "../lib/jsonLd";
import { Link } from "react-router-dom";

const categories = [
  { slug: "borrow", name: "Borrow", tagline: "Borrow smarter. Pay less.", description: "Understand loans, credit, debt, and the true cost of borrowing before you commit." },
  { slug: "invest", name: "Invest", tagline: "Put your money to work.", description: "Clear tools and guides for investing, retirement, and long-term wealth building." },
  { slug: "business", name: "Business", tagline: "Know your numbers. Grow with confidence.", description: "Practical financial tools for owners, operators, and entrepreneurs." },
  { slug: "real-estate", name: "Real Estate", tagline: "Make property decisions with the math in front of you.", description: "Calculators and guides for buying, financing, and investing in real estate." },
  { slug: "vehicles", name: "Vehicles", tagline: "See the full cost before you buy.", description: "Compare payments, ownership costs, financing, and vehicle decisions." },
];

const featuredCalcs = [
  { slug: "loan-payment", name: "Loan Payment Calculator", summary: "Estimate a fixed loan payment and total interest before you borrow.", category: "borrow", status: "live" },
  { slug: "compound-interest", name: "Compound Interest Calculator", summary: "Project how savings and recurring contributions may grow over time.", category: "invest", status: "live" },
  { slug: "net-worth", name: "Net Worth Calculator", summary: "Build a personal balance sheet by comparing assets with liabilities.", category: "invest", status: "live" },
  { slug: "debt-snowball", name: "Debt Snowball Calculator", summary: "Create a payoff schedule that targets smaller balances first.", category: "borrow", status: "live" },
];

const articles = [
  { slug: "complete-wealth-building-blueprint", title: "The Complete Wealth Building Blueprint: Save, Invest, Protect, and Grow", excerpt: "Organize cash flow, debt, investing, reserves, retirement, taxes, and protection into one practical system.", category: "invest", read_time: 4, author: "Robert Chandler", published_at: "2026-08-08" },
  { slug: "net-worth-growth-strategy", title: "Net Worth Growth Strategy: Build Assets, Reduce Debt, and Track Progress", excerpt: "Use assets, liabilities, savings rate, debt payoff, investing, and liquidity to improve your balance sheet.", category: "invest", read_time: 4, author: "Robert Chandler", published_at: "2026-08-08" },
  { slug: "sequence-of-returns-risk", title: "Sequence of Returns Risk: Why Timing Matters in Retirement Withdrawals", excerpt: "Understand why early retirement losses matter and how reserves, flexibility, and withdrawal planning can reduce damage.", category: "invest", read_time: 5, author: "Robert Chandler", published_at: "2026-08-08" },
];

export default function Home() {
  useSeo({
    title: "Make smarter money decisions",
    description:
      "Free calculators and plain-English guides for borrowing, investing, business, real estate, and vehicles.",
    path: "/",
  });

  useJsonLd("organization", buildOrganizationSchema());
  useJsonLd("website", buildWebSiteSchema());

  return (
    <>
      <Hero />

      <section className="border-b border-slate-200 bg-white py-14">
        <div className="mml-container max-w-3xl text-center">
          <p className="mml-overline">What is MyMoneyLocal?</p>
          <p className="mt-4 text-xl leading-relaxed text-slate-700 sm:text-2xl">
            A free, ad-light personal finance hub. Use the calculators to run
            the numbers before a big decision, read the guides to understand
            what the numbers mean, and skip the financial jargon while you do
            it.
          </p>
        </div>
      </section>

      <div id="categories" />
      <CategoryGrid categories={categories} />

      <section className="bg-[#F8FAF9] mml-section">
        <div className="mml-container">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
            <div className="max-w-xl">
              <p className="mml-overline">Featured calculators</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Run the numbers in under two minutes.
              </h2>
            </div>
            <Link to="/calculators" className="mml-link text-sm">
              Explore calculators →
            </Link>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {featuredCalcs.map((c) => (
              <CalculatorCard key={c.slug} calc={c} featured />
            ))}
          </div>
        </div>
      </section>

      <section className="mml-section">
        <div className="mml-container">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
            <div className="max-w-xl">
              <p className="mml-overline">Featured articles</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Read one. Save real money.
              </h2>
            </div>
            <Link to="/articles" className="mml-link text-sm">
              All articles →
            </Link>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((a) => (
              <ArticleCard key={a.slug} article={a} featured />
            ))}
          </div>
        </div>
      </section>

      <NextSteps />

      <section className="mml-section">
        <div className="mml-container max-w-3xl">
          <NewsletterSignup
            sourcePage="home"
            idScope="home"
            heading="Get one practical money email per week."
            subheading="No fluff. No ads. Unsubscribe with one click."
          />
        </div>
      </section>
    </>
  );
}
